import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtInt, STATUS_LABELS, STATUS_ORDER, orderCupsCount, orderMatchesSearch } from "@/lib/api";
import { useCanAdmin } from "@/lib/useCanAdmin";
import { Lock, X, MagnifyingGlass, ArrowRight, Warning, ArrowsClockwise, ArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import ScannerBar from "@/components/ScannerBar";
import OrderThumbs from "@/components/orders/OrderThumbs";
import StageSelector from "@/components/kanban/StageSelector";
import { useRealtimeOrders } from "@/lib/useRealtimeOrders";
import { useEscClose } from "@/lib/useEscClose";

const todayLocal = () => new Date().toLocaleDateString("en-CA");

function overdueDays(o) {
  if (!o.exit_date || o.production_status === "enviado") return 0;
  const exit = o.exit_date.slice(0, 10);
  const today = todayLocal();
  if (exit >= today) return 0;
  return Math.round((new Date(today) - new Date(exit)) / 86400000);
}

function AdvanceModal({ order, initialToStatus, onDone, onConflict, onCancel }) {
  const [toStatus, setToStatus] = useState(initialToStatus);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEscClose(onCancel);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post(`/orders/${order.id}/move`, {
        to_status: toStatus,
        collaborator_code: code,
        collaborator_password: password,
        expected_from_status: order.production_status,
      });
      onDone(toStatus);
    } catch (err) {
      // Conflito: outra estação já moveu este pedido — fecha e atualiza a tela
      if (err?.response?.status === 409) {
        toast.error(err.response.data?.detail || "Pedido já movido por outra estação.", { duration: 6000 });
        onConflict?.();
        return;
      }
      setError(err?.response?.data?.detail || "Falha na autenticação");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onCancel}>
      <div className="glass border-t-2 sm:border-2 w-full sm:max-w-md p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="panel-advance-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lock size={18} weight="bold" />
            <div className="overline">CONFIRMAR ETAPA</div>
          </div>
          <button onClick={onCancel}><X size={22} /></button>
        </div>
        <div className="text-sm mb-4">
          Pedido <span className="font-mono font-bold">#{order.bling_number}</span>
          <div className="flex items-center gap-2 mt-1 text-xs">
            <span className="font-semibold">{STATUS_LABELS[order.production_status]}</span>
            <ArrowRight size={12} />
            <span className="font-semibold text-[#00A300]">{STATUS_LABELS[toStatus]}</span>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <StageSelector fromStatus={order.production_status} value={toStatus} onChange={setToStatus} />
          <div>
            <label className="label">Código do colaborador</label>
            <input className="input text-lg py-3 font-mono" autoFocus required inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} data-testid="panel-collab-name" />
          </div>
          <div>
            <label className="label">Sua senha</label>
            <input type="password" className="input text-lg py-3" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="panel-collab-password" />
          </div>
          {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] px-3 py-2 text-sm" data-testid="panel-advance-error">{error}</div>}
          <button type="submit" disabled={loading} className="btn w-full py-4 text-base" data-testid="panel-advance-confirm">
            {loading ? "Validando..." : "Confirmar movimentação"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function FactoryPanel() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("separacao");
  const [search, setSearch] = useState("");
  const [advancing, setAdvancing] = useState(null); // order
  const [scanValue, setScanValue] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [showTop, setShowTop] = useState(false);
  const [counts, setCounts] = useState(null); // contagens exatas por status (cache do servidor)
  const [visibleCount, setVisibleCount] = useState(50);
  const [thumbs, setThumbs] = useState({}); // id -> {image_url, item_images}
  const { canAdmin } = useCanAdmin();

  // Artes carregadas à parte (payload pesado) — 1x no mount; board fica leve
  useEffect(() => {
    api.get("/orders/board/thumbs")
      .then(({ data }) => setThumbs(data || {}))
      .catch((e) => console.warn("thumbs load:", e?.message));
  }, []);

  const withArt = (o) => {
    const t = thumbs[o.id];
    if (!t) return o;
    return {
      ...o,
      image_url: t.image_url || null,
      order_items: (o.order_items || []).map((it, i) => ({ ...it, image_url: t.item_images?.[i] || null })),
    };
  };

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const focusOrder = async (raw) => {
    const num = String(raw || "").trim().replace(/^#/, "");
    if (!num) return;
    // Varredura em TODAS as colunas: nº do pedido, contato, telefone, pedido na loja
    let found = orders.find((o) => String(o.bling_number || "") === num) ||
      orders.find((o) => orderMatchesSearch(o, num));
    let list = orders;
    if (!found) {
      // Pode ainda não estar carregado (carga parcial de 50/coluna) — busca no servidor
      try {
        const { data } = await api.get("/orders", { params: { search: num, limit: 5 } });
        const items = data?.items || [];
        const hit = items.find((o) => String(o.bling_number || "") === num) || items[0];
        if (hit) {
          found = hit;
          if (!list.some((p) => p.id === hit.id)) {
            list = [...list, hit];
            setOrders(list);
          }
        }
      } catch (e) {
        console.warn("focusOrder server search:", e?.message);
      }
    }
    if (!found) {
      toast.error(`Nenhum pedido encontrado para "${num}".`, { duration: 3000 });
      setScanValue("");
      return;
    }
    // Muda para a coluna correspondente e limpa filtros para que o card apareça
    setStage(found.production_status);
    setSearch("");
    // Expande a paginação visual até a posição do pedido dentro da etapa
    const idx = list
      .filter((o) => o.production_status === found.production_status)
      .sort((a, b) => (a.exit_date || "9999").localeCompare(b.exit_date || "9999"))
      .findIndex((o) => o.id === found.id);
    setVisibleCount((v) => Math.max(v, idx + 10));
    setHighlightId(found.id);
    setScanValue("");
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="panel-card-${found.bling_number}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    setTimeout(() => setHighlightId(null), 4000);
    toast.success(`Pedido #${found.bling_number} · ${found.customer}`, { duration: 3000 });
  };

  const handleScan = () => focusOrder(scanValue);

  // Garantia pós-scan: quando os dados completos chegam durante o destaque,
  // re-expande a paginação até a posição real do pedido e re-rola até o card.
  useEffect(() => {
    if (!highlightId) return;
    const target = orders.find((o) => o.id === highlightId);
    if (!target || target.production_status !== stage) return;
    const idx = orders
      .filter((o) => o.production_status === stage)
      .sort((a, b) => (a.exit_date || "9999").localeCompare(b.exit_date || "9999"))
      .findIndex((o) => o.id === highlightId);
    if (idx >= 0) setVisibleCount((v) => (idx >= v ? idx + 10 : v));
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="panel-card-${target.bling_number}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    return () => clearTimeout(t);
  }, [highlightId, orders, stage]);

  const applyBoard = (data) => {
    setOrders(data?.items || (Array.isArray(data) ? data : []));
    if (data?.counts) setCounts(data.counts);
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (!silent) {
        // Estágio 1: os 50 mais urgentes de cada coluna — render imediato
        const { data } = await api.get("/orders/board", { params: { per_status: 50 } });
        applyBoard(data);
        setLoading(false);
      }
      // Estágio 2 (ou reload silencioso): board completo em background
      const { data } = await api.get("/orders/board");
      applyBoard(data);
    } catch (e) {
      console.warn("FactoryPanel orders load:", e?.response?.status || e?.message);
      if (!silent) setOrders([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(() => load(true), 30000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tempo real: recarrega quando qualquer estação movimenta um pedido
  useRealtimeOrders(() => load(true));

  // Busca manual: se nada bateu localmente (carga parcial), consulta o servidor
  // e mescla os resultados — a varredura cobre TODAS as etapas e até arquivados recentes.
  useEffect(() => {
    const q = search.trim();
    if (!q) return undefined;
    const t = setTimeout(async () => {
      if (orders.some((o) => orderMatchesSearch(o, q))) return;
      try {
        const { data } = await api.get("/orders", { params: { search: q, limit: 20 } });
        const items = data?.items || [];
        if (items.length) {
          setOrders((prev) => {
            const ids = new Set(prev.map((p) => p.id));
            const add = items.filter((i) => !ids.has(i.id));
            return add.length ? [...prev, ...add] : prev;
          });
        }
      } catch (e) {
        console.warn("panel search fallback:", e?.message);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search, orders]);

  const searching = !!search.trim();
  const nextStatusOf = (o) => STATUS_ORDER[STATUS_ORDER.indexOf(o.production_status) + 1] || null;
  // Contagem exata do servidor (cacheada); fallback: conta o que está carregado
  const countByStage = (s) => (counts ? (counts[s] ?? 0) : orders.filter((o) => o.production_status === s).length);
  // Com busca ativa, varre TODAS as etapas (independente da coluna selecionada)
  const fullList = orders
    .filter((o) => (searching ? true : o.production_status === stage))
    .filter((o) => orderMatchesSearch(o, search))
    .sort((a, b) => (a.exit_date || "9999").localeCompare(b.exit_date || "9999"));
  const list = fullList.slice(0, visibleCount);
  const remaining = (search ? fullList.length : Math.max(fullList.length, countByStage(stage))) - list.length;

  return (
    <div className="p-3 sm:p-5 md:p-8 max-w-3xl mx-auto" data-testid="factory-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="overline text-zinc-500">OPERAÇÃO DA FÁBRICA</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tighter">Painel da Fábrica</h1>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost !py-2 !px-2.5" onClick={() => load()} data-testid="panel-refresh">
            <ArrowsClockwise size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Stage selector */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-3" data-testid="panel-stage-selector">
        {STATUS_ORDER.filter((s) => s !== "enviado").map((s) => (
          <button
            key={s}
            onClick={() => { setStage(s); setVisibleCount(50); }}
            className={`px-1.5 py-1.5 border text-[9px] sm:text-[10px] font-bold uppercase tracking-tight flex flex-col items-center justify-center gap-1 leading-tight text-center transition-colors ${stage === s ? "stage-chip-active" : "stage-chip"}`}
            data-testid={`panel-stage-${s}`}
          >
            <div className="flex items-center gap-1">
              <span className={`dot ${s}`} />
              <span className="whitespace-normal">{STATUS_LABELS[s]}</span>
            </div>
            <span className="font-mono text-[11px]">{(s !== "em_desenvolvimento" || canAdmin) ? countByStage(s) : "—"}</span>
          </button>
        ))}
      </div>

      <div className="relative mb-2">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          className="input pl-8 !py-2 text-xs"
          placeholder="Buscar em todas as etapas: nº, cliente, contato ou pedido na loja"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="panel-search"
        />
      </div>

      {/* Scanner de código de barras — flutua no topo quando a página rola */}
      <ScannerBar
        value={scanValue}
        onChange={setScanValue}
        onScan={handleScan}
        onCameraResult={(code) => { setScanValue(code); focusOrder(code); }}
        testidPrefix="panel"
        className="mb-4"
      />

      {loading ? (
        <div className="text-zinc-400 py-10 text-center">Carregando…</div>
      ) : list.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-zinc-400">
          {searching ? "Nenhum pedido encontrado em nenhuma etapa." : `Nenhum pedido em ${STATUS_LABELS[stage]}.`}
        </div>
      ) : (
        <div className="space-y-3" data-testid="panel-order-list">
          {searching && (
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500" data-testid="panel-search-scope">
              Buscando em todas as etapas · {fullList.length} resultado{fullList.length !== 1 ? "s" : ""}
            </div>
          )}
          {list.map((o) => {
            const late = overdueDays(o);
            const isHl = highlightId === o.id;
            return (
              <div
                key={o.id}
                className={`panel-card p-4 ${isHl ? "kanban-card-highlight" : ""} ${late > 0 && !isHl ? "panel-card-late" : ""}`}
                data-testid={`panel-card-${o.bling_number}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <button className="font-mono text-2xl font-bold underline-offset-4 hover:underline" onClick={() => navigate(`/orders/${o.id}`)}>
                        #{o.bling_number}
                      </button>
                      {searching && (
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border border-black px-2 py-1" data-testid={`panel-stage-badge-${o.bling_number}`}>
                          <span className={`dot ${o.production_status}`} /> {STATUS_LABELS[o.production_status]}
                        </span>
                      )}
                      {late > 0 && (
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#E60000] text-white px-2 py-1">
                          <Warning size={11} weight="fill" /> {late}d atraso
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-semibold truncate">{o.customer}</div>
                    <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span><strong className="text-black">{fmtInt(orderCupsCount(o))}</strong> copos</span>
                      <span>silk: <strong className="text-black">{o.silk_color || "—"}</strong></span>
                      {o.exit_date && (
                        <span className={late > 0 ? "text-[#E60000] font-bold" : ""}>
                          limite: {new Date(o.exit_date.slice(0, 10) + "T12:00:00").toLocaleDateString("pt-BR")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <OrderThumbs order={withArt(o)} sizePx={75} max={2} hoverZoom testid={`panel-thumbs-${o.bling_number}`} />
                  </div>
                </div>
                {nextStatusOf(o) && (
                  <button
                    className="btn w-full mt-3 py-4 text-sm"
                    onClick={() => setAdvancing(o)}
                    data-testid={`panel-advance-${o.bling_number}`}
                  >
                    Concluir etapa <ArrowRight size={16} weight="bold" /> {STATUS_LABELS[nextStatusOf(o)]}
                  </button>
                )}
              </div>
            );
          })}
          {remaining > 0 && (
            <button
              className="w-full py-3 border border-dashed border-zinc-400 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white hover:border-black transition-colors"
              onClick={() => setVisibleCount((v) => v + 50)}
              data-testid="panel-show-more"
            >
              Mostrar mais ({remaining} restantes)
            </button>
          )}
        </div>
      )}

      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-5 right-5 z-40 bg-black text-white w-14 h-14 flex flex-col items-center justify-center shadow-2xl hover:bg-zinc-800 transition-colors"
          data-testid="panel-top-btn"
          aria-label="Voltar ao topo"
        >
          <ArrowUp size={20} weight="bold" />
          <span className="text-[8px] font-bold tracking-widest mt-0.5">TOPO</span>
        </button>
      )}

      {advancing && (
        <AdvanceModal
          order={advancing}
          initialToStatus={nextStatusOf(advancing)}
          onConflict={() => {
            setAdvancing(null);
            load(true);
          }}
          onDone={(toStatus) => {
            // Atualização otimista: tira o card da lista na hora e reconcilia em background
            const movedId = advancing.id;
            setAdvancing(null);
            setOrders((prev) => prev.map((x) => x.id === movedId ? { ...x, production_status: toStatus } : x));
            setSearch("");
            setScanValue("");
            window.scrollTo({ top: 0, behavior: "smooth" });
            load(true);
          }}
          onCancel={() => setAdvancing(null)}
        />
      )}
    </div>
  );
}
